import { camelizeKeys, decamelizeKeys } from 'humps';

import type { DynamicFlowItem, DynamicSession } from '../types/dynamicAssessment';
import type { DynamicAssessmentApiClient } from '../types/dynamicAssessmentApiClient';

export const PATHS = {
  SESSIONS: '/api/v2/dynamic-assessment-sessions/',
  FLOWS: '/api/v2/dynamic-assessment-sessions/flows/',
} as const;

// The onboarding assessment flows served by the dynamic-assessment sessions API, in
// serve order. Mirrors Sparta's survey-region order (health conditions → goals →
// assigned screeners (AOM + state + lab preference) → diabetes inclusion →
// diabetes decision → pre-diabetes → phq/gad). A future screener NOT supported by
// this endpoint stays on the legacy assigned-screener flow (see
// DYNAMIC_LEGACY_SCREENER_KEYS).
// Notes:
//   - the AOM refill / GLP-1 / state / lab-preference keys differ from their
//     legacy ProgramUnit.key counterparts ('aom_refill_date_screener_v1' /
//     'aom_specific_glp1_screener_v1' / 'us_state_screener_v1' /
//     'lab_preference_screener_v1');
//   - 'diabetes_inclusion_v1' is the diabetes acuity / inclusion screener
//     (type → insulin → pump, with server start-gates for no-diabetes skip and
//     high-MMS complete). Distinct from 'diabetes_decision_v1';
//   - 'diabetes_decision_v1' is the dynamic counterpart of the legacy diabetes
//     TYPE screeners — webserver's `legacy_sync/diabetes.py` syncs it to
//     'diabetes_type_screener_v1', 'insulin_pump_screener_v1' and
//     'diabetes_medication_management_consent_screener_v1', and its start gate
//     requires the member to already HAVE diabetes. It is NOT the pre-diabetes
//     risk screener;
//   - 'pre_diabetes_v1' is the counterpart of the standalone pre-diabetes screen
//     (the ADA "Health Risk Survey" behind /api/v4/pre-diabetes/, scored into the
//     `prediabetes-risk` metric point), and 'phq_gad_v1' of the PHQ/GAD
//     questionnaire. Their org gating (preDiabetesRequired / phqGadRequired) is
//     evaluated SERVER-side by the flows' routing gates: an unrequired flow
//     instant-skips to a terminal.
export const DYNAMIC_ONBOARDING_FLOW_KEYS = [
  'health_conditions_v1',
  'health_goals_v1',
  'aom_screener_v1',
  'aom_refill_date_v1',
  'aom_specific_glp1_v1',
  'us_state_v1',
  'lab_preference_v1',
  'diabetes_inclusion_v1',
  'diabetes_decision_v1',
  'pre_diabetes_v1',
  'phq_gad_v1',
] as const;

export type DynamicOnboardingFlowKey = (typeof DYNAMIC_ONBOARDING_FLOW_KEYS)[number];

// ---------------------------------------------------------------------------
// Response validation
// ---------------------------------------------------------------------------

/**
 * These endpoints hand us JSON we then cast to our wire types, and an unchecked
 * cast is what turns a bad response into a WRONG one rather than a failed one.
 *
 * The injected client is supposed to reject a non-2xx status (see
 * `DynamicAssessmentApiClient`), but nothing in the type system enforces that — a client
 * that resolves instead delivers an error body straight into these casts. The
 * damage is silent and specific: a non-array from `/flows/` used to become `[]`,
 * which the session hook cannot distinguish from "no flows left", so it fires
 * EXHAUSTION — the assessment reports itself complete. Likewise an error body cast to
 * `DynamicSession` yields a session with no `sessionUuid` and no `uiPayload`,
 * which reads as a flow to skip.
 *
 * Throwing instead lands in the hook's `catch`, which sets `error` and reports
 * `onDone('error')` — the designed failure path, where the host latches the
 * error and abandons the assessment region deliberately instead of pretending it
 * finished.
 *
 * An empty array stays valid: a member with no published flows is a real state.
 */
function describeShape(data: unknown): string {
  if (data === null) return 'null';
  if (Array.isArray(data)) return 'array';
  return typeof data;
}

function asFlowList(data: unknown): DynamicFlowItem[] {
  if (!Array.isArray(data)) {
    throw new Error(
      `GET ${PATHS.FLOWS} expected an array of flows, received ${describeShape(data)}. ` +
        'This usually means the injected api client resolved a non-2xx response ' +
        'instead of rejecting it.'
    );
  }
  return camelizeKeys(data) as DynamicFlowItem[];
}

function asSession(data: unknown, path: string): DynamicSession {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(
      `POST ${path} expected a dynamic-assessment session, received ${describeShape(data)}.`
    );
  }
  const session = camelizeKeys(data) as DynamicSession;
  if (typeof session.sessionUuid !== 'string') {
    throw new Error(
      `POST ${path} returned an object with no sessionUuid — not a dynamic-assessment ` +
        'session. This usually means the injected api client resolved a non-2xx ' +
        'response instead of rejecting it.'
    );
  }
  return session;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Create a new session for `flowKey`, or return the existing ACTIVE session if
 * one already exists (idempotent). The returned session's current node is the
 * `start` gate (node_type: 'info') — call `transitionSession` with an empty
 * answers map to advance past it.
 */
export async function startOrResumeSession(
  client: DynamicAssessmentApiClient,
  flowKey: string
): Promise<DynamicSession> {
  const res = await client.post(PATHS.SESSIONS, decamelizeKeys({ flowKey }));
  return asSession(res.data, PATHS.SESSIONS);
}

/**
 * Submit answers for the current node and advance the session.
 *
 * - For `info` (gate) nodes: pass `answers = {}`.
 * - For `question` nodes: pass `answers = { [signalKey]: value }`.
 *
 * `expectedVersion` should mirror the last received `session.version` to guard
 * against concurrent updates (409 Conflict on mismatch).
 *
 * `answers` is sent as-is, NOT through `decamelizeKeys` — only the wrapper
 * (`expectedSessionVersion`) is decamelized. This used to run the whole body
 * (wrapper + `answers`) through one deep `decamelizeKeys` call, which recurses
 * into `answers` and would snake_case nested object keys too. That was a
 * no-op for every answer shape that existed at the time (top-level answer
 * keys are SurveyJS element names / the server's `allowed_signal_key`s,
 * already snake_case, and values were plain strings/arrays-of-strings with no
 * nested keys of their own) — but a `repeatingList` (SurveyJS `matrixdynamic`)
 * answer is an array of row OBJECTS keyed by column name
 * (`RepeatingListRow`), and those column-name keys are nested object keys
 * that the deep transform would mangle, desyncing them from the `columns[].name`
 * the schema (and the member's saved answer, on any future read/pre-fill path)
 * expects. Splitting the transform like this was flagged as the fix here
 * before any such type existed — see git history on this file.
 */
export async function transitionSession(
  client: DynamicAssessmentApiClient,
  sessionUuid: string,
  answers: Record<string, unknown>,
  expectedVersion?: number
): Promise<DynamicSession> {
  const res = await client.post(`${PATHS.SESSIONS}${sessionUuid}/transition/`, {
    answers,
    ...(expectedVersion !== undefined
      ? decamelizeKeys({ expectedSessionVersion: expectedVersion })
      : {}),
  });
  return asSession(res.data, `${PATHS.SESSIONS}${sessionUuid}/transition/`);
}

/**
 * Returns the full list of published flows with their per-user status.
 * Filtered to the configured onboarding flows by the caller.
 */
export async function fetchDynamicFlows(
  client: DynamicAssessmentApiClient,
  signal?: AbortSignal
): Promise<DynamicFlowItem[]> {
  const res = await client.get(PATHS.FLOWS, { signal });
  return asFlowList(res.data);
}
